import { useQuery } from '@tanstack/react-query'
import { BookOpen, Clock, DoorOpen, GraduationCap, Mail, Phone, UsersRound } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { EmptyState, ErrorState, LoadingState } from '@/components/feedback/States'
import { Avatar } from '@/components/ui/Avatar'
import { Badge } from '@/components/ui/Badge'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { DescriptionList } from '@/components/ui/DescriptionList'
import { PageHeader } from '@/components/ui/PageHeader'
import { useAuth } from '@/features/auth/useAuth'
import { familyQuery, type FamilyChild } from '@/features/portal/api'
import { SubjectChips } from '@/features/portal/SubjectChips'
import { studentStatusTone } from '@/features/students/status'
import { familyPortalLabel, useGuardians } from '@/features/auth/guardians'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { cn } from '@/utils/cn'
import { ageFrom, formatDate, humanize } from '@/utils/format'
import { sectionLabel } from '@/features/academic/sectionLabel'
import { ReenrollmentCard } from '@/features/promotion/components/ReenrollmentWizard'

function ChildView({ child }: { child: FamilyChild }) {
  const { t } = useTranslation('portal')
  const guardians = useGuardians()
  const s = child.student
  const current = s.currentEnrollment
  const age = ageFrom(s.dateOfBirth)
  return (
    <div className="space-y-6">
      <Card className="overflow-hidden">
        <div className="bg-brand-gradient p-6 text-white">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
            <Avatar name={s.fullName} src={s.photoUrl} size="xl" className="ring-4 ring-white/30" />
            <div className="min-w-0 flex-1">
              <p className="text-2xl font-semibold">{s.fullName}</p>
              <p className="mt-1 text-sm text-white/80">
                {current
                  ? `${sectionLabel(current.className, current.sectionName ? t('Section {{name}}', { name: current.sectionName }) : '', ' · ')}${current.rollNumber ? ` · ${t('Roll {{number}}', { number: current.rollNumber })}` : ''}`
                  : t('Not currently enrolled')}
              </p>
              <p className="mt-1 font-mono text-xs text-white/70">{s.studentCode}</p>
            </div>
            <Badge tone={studentStatusTone[s.status]} className="self-start bg-white/90">
              {humanize(s.status)}
            </Badge>
          </div>
        </div>
        <CardBody className="grid gap-4 sm:grid-cols-3">
          <div className="flex items-center gap-3">
            <span className="grid size-10 place-items-center rounded-xl bg-brand-50 text-brand-600">
              <GraduationCap className="size-5" aria-hidden="true" />
            </span>
            <div>
              <p className="text-xs text-muted">{t('Academic year')}</p>
              <p className="font-medium text-ink">{current?.academicYear ?? '—'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="grid size-10 place-items-center rounded-xl bg-brand-50 text-brand-600">
              <DoorOpen className="size-5" aria-hidden="true" />
            </span>
            <div>
              <p className="text-xs text-muted">{t('Classroom')}</p>
              <p className="font-medium text-ink">{child.room ?? '—'}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="grid size-10 place-items-center rounded-xl bg-brand-50 text-brand-600">
              <Clock className="size-5" aria-hidden="true" />
            </span>
            <div>
              <p className="text-xs text-muted">{t('Shift')}</p>
              <p className="font-medium text-ink">
                {child.shift
                  ? `${child.shift.name}${child.shift.startTime ? ` · ${child.shift.startTime}–${child.shift.endTime ?? ''}` : ''}`
                  : '—'}
              </p>
            </div>
          </div>
        </CardBody>
      </Card>

      <ReenrollmentCard studentId={s.id} name={s.fullName} onlyWhenOpen />

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title={t('Class teacher')} icon={<UsersRound />} />
          <CardBody>
            {child.classTeacher ? (
              <div className="flex items-center gap-3">
                <Avatar name={child.classTeacher.name} src={child.classTeacher.photoUrl} size="lg" />
                <div className="min-w-0 space-y-1 text-sm">
                  <p className="font-semibold text-ink">{child.classTeacher.name}</p>
                  {child.classTeacher.email ? (
                    <a
                      href={`mailto:${child.classTeacher.email}`}
                      className="flex items-center gap-1.5 break-all text-brand-700 hover:underline"
                    >
                      <Mail className="size-3.5" aria-hidden="true" />
                      {child.classTeacher.email}
                    </a>
                  ) : null}
                  {child.classTeacher.phone ? (
                    <a
                      href={`tel:${child.classTeacher.phone}`}
                      className="flex items-center gap-1.5 text-ink-soft hover:text-brand-700"
                    >
                      <Phone className="size-3.5" aria-hidden="true" />
                      {child.classTeacher.phone}
                    </a>
                  ) : null}
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted">{t('No class teacher assigned yet.')}</p>
            )}
          </CardBody>
        </Card>
        <Card>
          <CardHeader title={t('Subjects this year')} icon={<BookOpen />} />
          <CardBody>
            <SubjectChips subjects={child.subjects} />
          </CardBody>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader title={t('Profile')} />
          <CardBody>
            <DescriptionList
              items={[
                {
                  label: t('Date of birth'),
                  value: s.dateOfBirth
                    ? `${formatDate(s.dateOfBirth)}${age !== null ? ` (${t('{{count}} yrs', { count: age })})` : ''}`
                    : '—',
                },
                { label: t('Gender'), value: humanize(s.gender) },
                { label: t('Blood group'), value: child.bloodGroup ?? '—' },
                { label: t('Admitted'), value: formatDate(s.admissionDate) },
              ]}
            />
          </CardBody>
        </Card>
        {guardians ? (
          <Card>
            <CardHeader title={t('Guardians on record')} />
            <ul className="divide-y divide-line">
              {child.guardians.map((g) => (
                <li key={g.id} className="flex items-center gap-3 px-5 py-3">
                  <Avatar name={g.name} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-ink">{g.name}</p>
                    <p className="text-xs text-muted">
                      {humanize(g.relation)}
                      {g.phone ? ` · ${g.phone}` : ''}
                    </p>
                  </div>
                  {g.isPrimary ? (
                    <Badge tone="brand" size="sm">
                      {t('Primary')}
                    </Badge>
                  ) : null}
                </li>
              ))}
            </ul>
          </Card>
        ) : null}
      </div>
    </div>
  )
}

export function FamilyDashboardPage() {
  const { t } = useTranslation('portal')
  useDocumentTitle(familyPortalLabel(useGuardians()))
  const { user } = useAuth()
  const { data, isPending, error, refetch } = useQuery(familyQuery())
  const [selected, setSelected] = useState(0)

  if (isPending) return <LoadingState />
  if (error) return <ErrorState onRetry={() => void refetch()} />

  const child = data[selected] ?? data[0]

  return (
    <>
      <PageHeader
        title={t('Hello, {{name}}', { name: user?.name.split(' ')[0] ?? '' })}
        description={
          user?.role === 'parent'
            ? t('An overview of your children at school.')
            : t('Your school record at a glance.')
        }
      />
      {data.length > 1 ? (
        <div role="tablist" aria-label={t('Choose child')} className="mb-6 flex gap-2 overflow-x-auto">
          {data.map((c, i) => (
            <button
              key={c.student.id}
              role="tab"
              type="button"
              aria-selected={i === selected}
              onClick={() => setSelected(i)}
              className={cn(
                'flex shrink-0 items-center gap-2 rounded-full border py-1 pr-4 pl-1 text-sm font-medium transition-colors',
                i === selected
                  ? 'border-brand-300 bg-brand-50 text-brand-700'
                  : 'border-line bg-surface text-ink-soft hover:border-line-strong',
              )}
            >
              <Avatar name={c.student.fullName} src={c.student.photoUrl} size="sm" />
              {c.student.firstName}
            </button>
          ))}
        </div>
      ) : null}
      {child ? (
        <ChildView child={child} />
      ) : (
        <Card>
          <EmptyState
            icon={<UsersRound />}
            title={t('No student linked to your account')}
            description={t("Ask the school office to link your login to your child's record.")}
          />
        </Card>
      )}
    </>
  )
}
